import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const focusTrapStack: HTMLElement[] = [];

const useFocusTrap = <T extends HTMLElement>(
  onEscape: () => void,
): React.MutableRefObject<T | null> => {
  const containerRef = useRef<T | null>(null);
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    const previouslyFocusedElement =
      document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    if (!container) return;

    focusTrapStack.push(container);
    const focusable =
      container?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable?.[0] ?? container)?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (focusTrapStack[focusTrapStack.length - 1] !== container) return;

      if (e.key === "Escape") {
        e.preventDefault();
        onEscapeRef.current();
        return;
      }

      if (e.key !== "Tab") return;

      const elements = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (elements.length === 0) {
        e.preventDefault();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (!container.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const trapIndex = focusTrapStack.lastIndexOf(container);
      if (trapIndex !== -1) focusTrapStack.splice(trapIndex, 1);
      previouslyFocusedElement?.focus();
    };
  }, []);

  return containerRef;
};

export default useFocusTrap;
